import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ShoppingCart } from "lucide-react";
import { brand, navLinks } from "../data/siteData";
import { useCart } from "../context/CartContext";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProductOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 w-full bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-md" : "shadow-sm"
      }`}
    >
      <div className="h-1 w-full bg-p3-gold" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img src={brand.logo} alt={brand.name} className="h-14 w-auto object-contain" />
          <span className="hidden sm:block font-heading font-bold text-lg text-p3-navy leading-tight">
            Prime Path<br />Partners
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.dropdown ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setProductOpen(true)}
                onMouseLeave={() => setProductOpen(false)}
              >
                <button className="flex items-center gap-1 text-sm font-semibold tracking-wide text-p3-navy hover:text-p3-blue transition-colors uppercase">
                  {link.label} <ChevronDown size={15} />
                </button>
                {productOpen && (
                  <div className="absolute top-full left-0 pt-3 w-56">
                    <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-2 overflow-hidden">
                      {link.dropdown.map((d) => (
                        <Link
                          key={d.label}
                          to={d.href}
                          className="block px-4 py-2.5 text-sm text-p3-navy hover:bg-p3-cream hover:text-p3-blue transition-colors"
                        >
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="text-sm font-semibold tracking-wide text-p3-navy hover:text-p3-blue transition-colors uppercase"
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <Link to="/cart" className="relative text-p3-navy hover:text-p3-blue transition-colors">
            <ShoppingCart size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-p3-gold text-p3-navy text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
        </div>

        <button className="lg:hidden text-p3-navy" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-5 py-4 space-y-3">
          {navLinks.map((link) => (
            <div key={link.label}>
              <Link to={link.href} className="block py-2 text-p3-navy font-semibold uppercase text-sm">
                {link.label}
              </Link>
              {link.dropdown && (
                <div className="pl-4 space-y-1">
                  {link.dropdown.map((d) => (
                    <Link key={d.label} to={d.href} className="block py-1 text-sm text-p3-muted">
                      {d.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link to="/cart" className="block py-2 text-p3-navy font-semibold uppercase text-sm">
            Cart {count > 0 && `(${count})`}
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
